import type { PriceObservation, ShoppingListItem, Store } from '@recipes/contracts';
import type { ListEstimate } from '@recipes/domain';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@recipes/ui';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCatalogStores } from '@/features/catalog/hooks';
import { formatDay, formatMoney, useListEstimates, usePricesEnabled } from './hooks';
import { PriceDialog, type PriceTarget } from './price-dialog';

type Estimate = ListEstimate<PriceObservation>;

/**
 * "What will this list cost at …?" (docs/partial/shopping-in-store-spec.md 5.6, 6.7). The footer
 * shows the estimate for the store chosen in "Shopping at …"; the sheet adds every other store
 * the household has prices for. Totals with different coverage cannot be compared – only the
 * common basket names a cheapest store. Computed on the device, live, in the reader's currency.
 */
export function ListEstimateFooter({
  items,
  store,
}: {
  items: readonly ShoppingListItem[];
  store: Store | null;
}) {
  const { t } = useTranslation();
  const enabled = usePricesEnabled();
  const allStores = useCatalogStores();
  const estimates = useListEstimates(items, store, allStores);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<string | null>(null);
  const [priceFor, setPriceFor] = useState<PriceTarget | null>(null);
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  if (!enabled) return null;
  const { here, comparison, currency } = estimates;
  const openItems = items.filter((item) => !item.checked).length;
  const anyPrices = comparison.stores.some((estimate) => estimate.pricedItems > 0);
  if (openItems === 0 || (!anyPrices && !store)) return null;

  const nameOf = (storeId: string) =>
    estimates.stores.find((candidate) => candidate.id === storeId)?.name ?? '';
  const coverage = (estimate: Estimate) =>
    t('prices.estimate.coverage', { priced: estimate.pricedItems, total: estimate.openItems });
  const label =
    here && here.enough
      ? t(here.approximate ? 'prices.estimate.footerStale' : 'prices.estimate.footer', {
          total: formatMoney(here.total, currency),
          store: store?.name ?? '',
          coverage: coverage(here),
        })
      : here
        ? t('prices.estimate.tooFew', { store: store?.name ?? '', coverage: coverage(here) })
        : t('prices.estimate.ask');

  const compared = comparison.stores.filter((estimate) => estimate.enough);
  const tooFew = comparison.stores.filter(
    (estimate) => !estimate.enough && estimate.storeId !== here?.storeId,
  );
  const shown = detail ? comparison.stores.find((estimate) => estimate.storeId === detail) : null;

  return (
    <>
      <button
        type="button"
        // Above the main navigation of a phone, which owns the bottom edge.
        className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom))] z-10 mx-auto mt-4 flex min-h-[44px] w-full items-center justify-center rounded-full border bg-background/95 px-4 text-sm shadow-sm backdrop-blur md:bottom-2"
        onClick={() => {
          setDetail(null);
          setOpen(true);
        }}
        data-testid="list-estimate"
      >
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          closeLabel={t('common.close')}
          className="max-h-[90dvh] overflow-y-auto"
          data-testid="estimate-sheet"
        >
          <DialogHeader>
            <DialogTitle>{shown ? nameOf(shown.storeId) : t('prices.estimate.title')}</DialogTitle>
            <DialogDescription>{t('prices.estimate.description', { currency })}</DialogDescription>
          </DialogHeader>

          {!shown && (
            <div className="space-y-4">
              {!store && <p className="text-sm">{t('prices.estimate.chooseStore')}</p>}
              <ul className="divide-y rounded-lg border">
                {compared.map((estimate) => (
                  <li key={estimate.storeId}>
                    <button
                      type="button"
                      className="flex min-h-[44px] w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm"
                      onClick={() => setDetail(estimate.storeId)}
                      data-testid="estimate-store"
                    >
                      <span className="font-medium">
                        {nameOf(estimate.storeId)}
                        {estimate.storeId === here?.storeId && (
                          <Badge variant="muted" className="ml-2">
                            {t('prices.here')}
                          </Badge>
                        )}
                      </span>
                      <span className="text-right tabular-nums">
                        ≈ {formatMoney(estimate.total, currency)}
                        <span className="block text-xs text-muted-foreground">
                          {coverage(estimate)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
                {compared.length === 0 && (
                  <li className="p-3 text-sm text-muted-foreground">
                    {t('prices.estimate.noneEnough')}
                  </li>
                )}
              </ul>

              {comparison.commonBasket && (
                <div className="rounded-lg bg-muted/40 p-3 text-sm" data-testid="estimate-basket">
                  <p className="font-medium">
                    {t('prices.estimate.basket', { count: comparison.commonBasket.itemIds.length })}
                  </p>
                  <p className="tabular-nums">
                    {[...comparison.commonBasket.totals]
                      .sort((a, b) => Number(a.total) - Number(b.total))
                      .map(
                        (entry) => `${nameOf(entry.storeId)} ${formatMoney(entry.total, currency)}`,
                      )
                      .join(' · ')}
                  </p>
                  {comparison.commonBasket.cheapestStoreId && (
                    <p className="text-xs text-muted-foreground">
                      {t('prices.estimate.basketCheapest', {
                        store: nameOf(comparison.commonBasket.cheapestStoreId),
                      })}
                    </p>
                  )}
                </div>
              )}

              {tooFew.length > 0 && (
                <div data-testid="estimate-too-few">
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                    {t('prices.estimate.tooFewTitle')}
                  </h3>
                  <ul className="text-sm">
                    {tooFew.map((estimate) => (
                      <li key={estimate.storeId}>
                        <button
                          type="button"
                          className="min-h-[36px] underline"
                          onClick={() => setDetail(estimate.storeId)}
                        >
                          {nameOf(estimate.storeId)} · {coverage(estimate)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-xs text-muted-foreground">{t('prices.estimate.note')}</p>
            </div>
          )}

          {shown && (
            <div className="space-y-3">
              <Button variant="ghost" size="sm" onClick={() => setDetail(null)}>
                {t('prices.estimate.back')}
              </Button>
              <ul className="divide-y rounded-lg border text-sm">
                {shown.lines.map((line) => (
                  <li key={line.itemId} className="p-2" data-testid="estimate-line">
                    <div className="flex items-baseline justify-between gap-2">
                      <span>{itemsById.get(line.itemId)?.name}</span>
                      <span className="tabular-nums">
                        {line.stale && `${t('prices.approx')} `}
                        {formatMoney(line.amount, currency)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {line.productId && `${estimates.products.get(line.productId) ?? ''} · `}
                      {line.multiplier !== '1' && `${line.multiplier} × `}
                      {formatMoney(line.observation.price, currency)} ·{' '}
                      {formatDay(line.observation.observedAt)}
                      {line.viaChain && ` · ${t('prices.estimate.viaChain')}`}
                      {line.observation.isPromotion &&
                        ` · ${
                          line.observation.promotionEndsAt
                            ? t('prices.promotionUntil', {
                                date: formatDay(line.observation.promotionEndsAt),
                              })
                            : t('prices.promotion')
                        }`}
                    </p>
                  </li>
                ))}
              </ul>
              {shown.missing.length > 0 && (
                <div data-testid="estimate-missing">
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                    {t('prices.estimate.missing')}
                  </h3>
                  <ul className="divide-y rounded-lg border text-sm">
                    {shown.missing.map((itemId) => {
                      const item = itemsById.get(itemId);
                      if (!item) return null;
                      const priceable = Boolean(item.product?.id || item.ingredientId);
                      return (
                        <li key={itemId} className="flex items-center justify-between gap-2 p-2">
                          <span>{item.name}</span>
                          {priceable ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setPriceFor({
                                  productId: item.product?.id ?? null,
                                  ingredientId: item.ingredientId ?? null,
                                  name: item.product?.name ?? item.name,
                                })
                              }
                            >
                              <Plus aria-hidden /> {t('prices.add')}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {t('prices.estimate.noProduct')}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {priceFor && (
        <PriceDialog
          open
          onOpenChange={(next) => !next && setPriceFor(null)}
          target={priceFor}
          defaultStoreId={shown?.storeId ?? store?.id ?? null}
        />
      )}
    </>
  );
}
